import { Button } from "@fieldbook/ui/button"
import { useState } from "react"
import { appShell } from "@/platform"

const PERSIST_KEY = "fieldbook.persistData"

async function checkForUpdateAndReload() {
  if (!("serviceWorker" in navigator)) {
    location.reload()
    return
  }
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) {
    location.reload()
    return
  }
  await reg.update().catch(() => {})
  if (reg.waiting) {
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => location.reload(),
      { once: true },
    )
    reg.waiting.postMessage({ type: "SKIP_WAITING" })
    return
  }
  location.reload()
}

function resetCachedDataAndReload() {
  try {
    localStorage.removeItem(PERSIST_KEY)
  } catch {
    // ignore — reload anyway
  }
  location.reload()
}

type Props = {
  error: unknown
  resetError: () => void
  eventId?: string
}

export function ErrorFallback({ error, resetError, eventId }: Props) {
  const [busy, setBusy] = useState<null | "update" | "reset">(null)
  const [showReset, setShowReset] = useState(false)
  const supportsPwaUpdates = appShell.supportsPwaUpdates()

  const message =
    error instanceof Error ? error.message : "Something went wrong."

  const handleUpdate = async () => {
    setBusy("update")
    if (supportsPwaUpdates) {
      await checkForUpdateAndReload()
    } else {
      location.reload()
    }
  }

  const handleReset = () => {
    setBusy("reset")
    resetCachedDataAndReload()
  }

  return (
    <div className="bg-background text-foreground flex min-h-screen items-center justify-center p-6">
      {!showReset && (
        <div className="w-full max-w-md space-y-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground text-sm">
              The app hit an unexpected error. The error has been logged and
              Fieldwork Labs will be notified. Try one of the options below.
            </p>
          </div>

          <details className="dark:bg-muted text-muted-foreground rounded p-2 text-xs">
            <summary className="cursor-pointer">Error details</summary>
            <p className="mt-2 break-all">{message}</p>
            {eventId && <p className="mt-1 opacity-70">Event ID: {eventId}</p>}
          </details>

          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={resetError}>
              Try again
            </Button>

            <Button
              onClick={handleUpdate}
              disabled={busy !== null}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {busy === "update"
                ? supportsPwaUpdates
                  ? "Checking for update…"
                  : "Reloading…"
                : supportsPwaUpdates
                  ? "Check for update & reload"
                  : "Reload app"}
            </Button>
            <div className="mt-24 flex flex-col justify-center gap-2 rounded-lg border border-red-500/30 p-2">
              <p className="text-lg text-red-500">Danger zone</p>
              <Button
                onClick={() => setShowReset(true)}
                disabled={busy !== null}
                variant="ghost"
                className="border-destructive text-destructive hover:bg-destructive/10 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {busy === "reset" ? "Resetting…" : "Reset cached data & reload"}
              </Button>
              <p className="text-xs text-gray-600">
                Resetting clears locally cached data. Some local data may be
                lost — only use this if other options don&apos;t work.
              </p>
            </div>
          </div>
        </div>
      )}
      {showReset && (
        <div className="w-full max-w-md space-y-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Resetting Cached Data</h1>
            <p className="text-muted-foreground text-sm">
              In some cases, it's necessary to reset cached data. This will
              clear locally stored data and reload the app. You may need to
              reload your project data after resetting.
            </p>
            <span className="text-sm font-bold">What gets cleared?</span>
            <p className="text-muted-foreground text-sm">
              Project settings, organisation settings, and cached searches will
              be cleared. Observations, collections, survey sites, and other
              user-recorded data will{" "}
              <span className="text-foreground font-bold">not</span> be cleared.
            </p>
            <div className="flex w-full flex-col justify-center gap-4">
              <Button
                onClick={handleReset}
                disabled={busy !== null}
                variant="destructive"
              >
                {busy === "reset" ? "Resetting…" : "Reset cached data & reload"}
              </Button>
              <Button onClick={() => setShowReset(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
